import { type ReactElement, useEffect, useState } from "react"
import styled from "styled-components"
import type { ThumbnailCue } from "../lib/thumbnailsVtt"
import { formatTimecode } from "../lib/timecode"

/**
 * Figma's 390 × 229 preview at this frame's scale. Held in px, not rem, because
 * a sprite-sheet cue has to be scaled by a ratio against its own pixel crop.
 */
const PREVIEW_WIDTH_PX = 240
const PREVIEW_ASPECT = 16 / 9

const Root = styled.div<{ $left: number }>`
  position: absolute;
  bottom: calc(100% + ${({ theme }) => theme.spacing.sm});
  left: ${({ $left }) => `${$left}px`};
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  pointer-events: none;
  z-index: 2;
`

const Thumbnail = styled.div`
  position: relative;
  width: ${PREVIEW_WIDTH_PX}px;
  aspect-ratio: ${PREVIEW_ASPECT};
  border-radius: ${({ theme }) => theme.radius.md};
  overflow: hidden;
  background-color: ${({ theme }) => theme.player.track};
  box-shadow: 0 0 4px 0 rgb(0 0 0 / 0.7);
`

/**
 * Two stacked layers, not one.
 *
 * A cue change swaps the URL, and a bare `<img src>` swap paints the empty
 * container for a frame before the next image decodes — a flicker on every pixel
 * of a drag. The last decoded image stays underneath until the incoming one
 * fires `load`. This is the same trick the TV player uses for the FireTV
 * background flash.
 *
 * `$crop` is set only when the VTT indexes a sprite sheet, in which case the
 * image is blown up so the cue's rectangle fills the box and shifted so that
 * rectangle is what shows.
 */
const Layer = styled.img<{ $crop: ThumbnailCue["crop"] }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;

  ${({ $crop }) => {
    if (!$crop) {
      return ""
    }

    const scale = PREVIEW_WIDTH_PX / $crop.width

    return `
      inset: auto;
      width: auto;
      height: auto;
      max-width: none;
      object-fit: none;
      transform-origin: 0 0;
      transform: scale(${scale}) translate(${-$crop.x}px, ${-$crop.y}px);
    `
  }}
`

const Timecode = styled.span`
  padding: ${({ theme }) => `${theme.spacing.xxs} ${theme.spacing.xs}`};
  border-radius: ${({ theme }) => theme.radius.sm};
  background-color: ${({ theme }) => theme.player.track};
  color: ${({ theme }) => theme.player.textPrimary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  line-height: ${({ theme }) => theme.lineHeight.tight};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`

const cueKey = (cue: ThumbnailCue): string => `${cue.url}#${cue.crop?.x ?? 0},${cue.crop?.y ?? 0}`

interface ScrubPreviewProps {
  /** Where the preview points, in px from the left edge of the track. */
  readonly offsetPx: number
  readonly seconds: number
  /** Absent when the session has no thumbnail index, or between two cues. */
  readonly cue: ThumbnailCue | undefined
  readonly qaHook: string
}

/**
 * The bubble above the scrub bar: the frame the viewer would land on, and the
 * time they would land at. The timecode shows even with no thumbnails — a
 * session with no VTT still needs to say where a drag is going.
 */
export const ScrubPreview = ({ offsetPx, seconds, cue, qaHook }: ScrubPreviewProps): ReactElement => {
  const [decoded, setDecoded] = useState<ThumbnailCue | undefined>(undefined)

  useEffect(() => {
    if (!cue) {
      setDecoded(undefined)
    }
  }, [cue])

  const isDecoded = Boolean(cue && decoded && cueKey(cue) === cueKey(decoded))

  return (
    <Root $left={offsetPx} data-qa-hook={qaHook}>
      {cue || decoded ? (
        <Thumbnail>
          {decoded && !isDecoded ? <Layer src={decoded.url} alt="" $crop={decoded.crop} aria-hidden="true" /> : null}
          {cue ? (
            <Layer
              key={cueKey(cue)}
              src={cue.url}
              alt=""
              $crop={cue.crop}
              aria-hidden="true"
              onLoad={() => setDecoded(cue)}
            />
          ) : null}
        </Thumbnail>
      ) : null}
      <Timecode data-qa-hook={`${qaHook}-time`}>{formatTimecode(seconds)}</Timecode>
    </Root>
  )
}
